/**
 * Organization and Website Schema Components
 * From SchemaMarkup.tsx lines 60-195
 */

import { BASE_URL, CONTACT, SEO } from '@/lib/constants';

/**
 * Organization Schema (Global - for homepage)
 * Enhanced for AI discovery
 */
export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    '@id': `${BASE_URL}/#organization`,
    name: SEO.siteName,
    alternateName: ['Apex Andes', 'Apex Heliski', 'Apex Chile'],
    description: 'Premier luxury adventure travel company specializing in heli-skiing, fly fishing, and exclusive lodges in the Chilean Andes and Patagonia.',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo.svg`,
      width: 200,
      height: 60,
    },
    image: `${BASE_URL}/og-image.png`,
    telephone: CONTACT.whatsapp,
    email: CONTACT.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '330 Belleview Ave',
      addressLocality: 'Crested Butte',
      addressRegion: 'CO',
      postalCode: '81224',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -33.3547,
      longitude: -70.2451,
    },
    areaServed: [
      {
        '@type': 'Country',
        name: 'Chile',
      },
      {
        '@type': 'Place',
        name: 'Patagonia',
      },
      {
        '@type': 'Place',
        name: 'Chilean Andes',
      },
    ],
    sameAs: [
      'https://www.instagram.com/apexexperience/',
      'https://www.facebook.com/apexexperience',
      'https://twitter.com/apexexperience',
    ],
    foundingDate: '2020',
    knowsAbout: [
      'Heli-skiing',
      'Fly fishing',
      'Ski touring',
      'Backcountry skiing',
      'Cat skiing',
      'Luxury travel',
      'Adventure tourism',
      'Chilean Andes',
      'Patagonia',
      'Luxury lodges',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Adventure Experiences',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Heli-Skiing',
            description: 'Private helicopter access to 10,000+ vertical feet of untouched powder',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Fly Fishing',
            description: 'Trophy trout fishing in Patagonia\'s legendary rivers',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Luxury Lodge Accommodation',
            description: 'Six exclusive lodges in the Chilean Andes and Patagonia',
          },
        },
      ],
    },
    priceRange: '$$$$$',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    slogan: 'Unprecedented adventures at premier lodges',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

/**
 * Website Schema (Global)
 */
export function WebsiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    url: BASE_URL,
    name: SEO.siteName,
    description: SEO.defaultDescription,
    publisher: {
      '@id': `${BASE_URL}/#organization`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
